<template>
  <el-select
    v-model="valueBackup"
    :placeholder="placeholder"
    filterable
    remote
    :multiple="multiple"
    :collapse-tags="multiple"
    :size="size"
    :remote-method="handleRemote"
    :loading="isLoading"
    :clearable="clearable"
    :popper-class="isFilter ? 'select-detail-popper' : ''"
    @change="handleChange"
  >
    <el-option
      v-for="v in listBackup"
      :key="v.id"
      :label="formatOptionLabel(v)"
      :value="v.id"
      :disabled="isOptionDisabled(v)"
    >
      <!-- 筛选场景：两行展示「名称+已删除标记+面额 / 发放方式+ID」，便于区分重名类型 -->
      <v-select-option-detail
        v-if="isFilter"
        :title="v.voucher_type_name"
        :value="`${toYuan(v.amount_in_cents)} 元`"
        :meta="findVoucherIssueTypeName(v.issue_type)"
        :extra="`ID ${v.id}`"
        :deleted="isDeleted(v)"
      />
      <!-- 表单场景：沿用原来的完整类型详情 -->
      <template v-else>{{ formatVoucherLabel(v) }}</template>
    </el-option>
  </el-select>
</template>

<script>
import { index } from '@/api/voucher-type';
import { toYuan, formatDuration, timeToYmdHi } from '@/filters';
import { voucherIssueTypes, findVoucherIssueType } from '@/enums/voucherIssueTypes';

export default {
  name: 'VSelectVoucherType',
  props: {
    value: [Number, String, Array],
    list: { type: Array, default: () => [] },
    // 指定学员后，该学员已拥有的「每人限领一张」类型会被置灰
    studentId: [Number, String],
    multiple: Boolean,
    // 列表筛选场景：下拉项两行展示「类型名+已删除标记 / 面额+发放方式+ID」并一次多加载类型（含已删除类型）；
    // 选中项仍只显示类型名，避免多选 tag 过长
    isFilter: Boolean,
    // 发放方式过滤：默认沿用表单场景（仅手动办理）；筛选场景传 [] 表示全部
    issueTypes: { type: Array, default: () => [voucherIssueTypes.manualProcessing.value] },
    // 必须显式声明并透传给 el-select：否则父级传的 size 不会生效，高度会退回默认 40px
    size: { type: String, default: 'medium' },
    placeholder: { type: String, default: '请输入代金券类型名称搜索' },
    clearable: { type: Boolean, default: true },
  },
  data: () => ({
    valueBackup: '',
    listBackup: [],
    isLoading: false,
  }),
  watch: {
    value(value) {
      if (this.multiple) {
        const arr = Array.isArray(value) ? value : [];
        if (JSON.stringify(arr) !== JSON.stringify(this.valueBackup)) this.valueBackup = arr;
        return;
      }

      if (value !== this.valueBackup) this.valueBackup = +value || '';
    },
    valueBackup(valueBackup) {
      if (this.multiple) {
        if (JSON.stringify(valueBackup) !== JSON.stringify(this.value)) this.$emit('input', valueBackup || []);
        return;
      }

      if (valueBackup !== (+this.value || '')) this.$emit('input', valueBackup);
    },
    list(list) {
      if (JSON.stringify(list) !== JSON.stringify(this.listBackup)) this.listBackup = list || [];
    },
    studentId() {
      this.getList('');
    },
  },
  methods: {
    toYuan,
    formatDuration,
    // 「每人限领一张」且该学员已拥有 → 置灰，避免提交后才被后端拦下
    isOptionDisabled(v) {
      return Number(v.is_unique) === 1 && Number(v.is_owned) === 1;
    },
    // 指定时间过期的类型展示固定时间，否则展示有效时长；并展示「是否可重复拥有 / 是否可叠加」
    formatVoucherLabel(v) {
      const expireText = v.expire_mode === 1 && v.fixed_expire_time
        ? `${timeToYmdHi(v.fixed_expire_time)} 过期`
        : formatDuration(v.valid_duration_seconds);
      const limits = [Number(v.is_unique) === 1 ? '每人限领一张' : '可重复拥有'];
      limits.push(Number(v.is_stackable) === 1 ? '可叠加' : '不可叠加');
      if (Number(v.is_unique) === 1 && Number(v.is_owned) === 1) limits.push('该学员已拥有');
      return `${v.voucher_type_name}，${toYuan(v.amount_in_cents)} 元，${expireText}，${limits.join('，')}`;
    },
    // 类型是否已被删除（is_del 存删除时间戳，> 0 即已删除）
    isDeleted(v) {
      return Number(v.is_del) > 0;
    },
    // 发放方式名称：筛选下拉项里用于区分同名类型
    findVoucherIssueTypeName(issueType) {
      const [state] = findVoucherIssueType(Number(issueType));
      return state.name || '';
    },
    // 选中项只显示类型名（多选 tag 不宜过长），已删除的类型补标记，避免选中后看不出选的是历史类型
    formatOptionLabel(v) {
      if (!this.isFilter) return this.formatVoucherLabel(v);
      return this.isDeleted(v) ? `${v.voucher_type_name}（已删除）` : v.voucher_type_name;
    },
    handleChange(valueBackup) {
      if (this.multiple) return this.$emit('change', valueBackup || []);
      if (!valueBackup) return [];

      for (let i = 0; i < this.listBackup.length; i++) {
        if (this.listBackup[i].id === valueBackup) return this.$emit('change', this.listBackup[i]);
      }
    },
    async getList(keyword) {
      this.isLoading = true;
      const params = { keyword, is_disable_count: 1 };
      if (this.isFilter) {
        params.size = 200;
        // 筛选场景把已删除的类型一起查出来：历史券仍引用这些类型，运营回顾旧券时要能按它筛选
        params.is_not_filter_del = 1;
      }
      if (this.issueTypes && this.issueTypes.length) params.issue_types = this.issueTypes;
      if (this.studentId) params.student_id = this.studentId;
      const { isError, data } = await index(params);
      this.isLoading = false;
      if (!isError) this.listBackup = data.list;
    },
    handleRemote(keyword) {
      if (keyword) return this.getList(keyword);
      this.list = [];
    },
  },
  beforeMount() {
    if (this.multiple) {
      this.valueBackup = Array.isArray(this.value) ? this.value : [];
    } else {
      this.valueBackup = +this.value || '';
    }

    if (this.list.length) return this.listBackup = this.list;
    this.getList('');
  },
};
</script>
